/**
 * AgentsChat — agentic chat surface.
 *
 * Routes each user question to the most-relevant specialist persona
 * (Anika the style curator · Riaan the budget guardian · Anil the
 * supply-chain controller · Ananya the NKBA kitchen expert · …) by
 * keyword-matching against the agent registry's `scope` + label fields
 * in codegen.json#agents.
 *
 * Answers are constructed from the typed project graph — same merged
 * layers (sample + imported + activities + synth) Design, Project map
 * and BOM read. No external LLM call required for the rule-based
 * answers; the gateway path is for free-form follow-ups (queued via
 * E.0 ComfyUI / nemotron-nvfp4 route).
 *
 * Every exchange writes one human + one agent :TimelineEvent so the
 * Timeline panel stays in sync structurally.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Send, Sparkles, User, MessageSquare, Trash2, AlertTriangle } from "lucide-react";
import { useActiveProjectGraph } from "../../lib/useActiveProjectGraph";
import { mergeProjectLayers } from "../../lib/projectLayers";
import { resolveActor, recordEvent } from "../../lib/agent/timeline";
import { agentRegistry } from "../../lib/agent/orchestrator";
import { addUserTask } from "../../lib/userTasks";
import { askAgent } from "../../lib/agent/agentClient";

interface ChatMessage {
  readonly id: string;
  readonly role: "user" | "agent";
  readonly text: string;
  readonly actorId: string;          // user email or :Agent.id
  readonly persona?: ReturnType<typeof resolveActor>;
  readonly ts: string;
  readonly cited?: ReadonlyArray<string>;
}

/** Imperative write actions the chat can execute *client-side* without
 *  the agent brain — pure graph mutations whose intent is unambiguous
 *  from the verb. CLAUDE.md §5 — explicit, never silent. */
export interface ChatWriteAction {
  readonly kind: "addTask";
  readonly title: string;
  readonly dueHint?: string; // free-text e.g. "before handover"
}
function detectWriteAction(question: string): ChatWriteAction | null {
  const q = question.trim();
  const m = q.match(/^(?:add|create|new|schedule|note)\s+(?:a\s+)?task\s+(?:to\s+|for\s+)?(.+)$/i)
        ?? q.match(/^(?:remember\s+to|need\s+to|todo)\s*[:-]?\s+(.+)$/i);
  if (m && m[1]) {
    const rest = m[1].trim().replace(/^["']|["']$/g, "");
    const hintMatch = rest.match(/^(.+?)\s+(?:before|after|by|until|due)\s+(.+)$/i);
    if (hintMatch) {
      return { kind: "addTask", title: hintMatch[1]!.trim(), dueHint: hintMatch[2]!.trim() };
    }
    return { kind: "addTask", title: rest };
  }
  return null;
}

// askAgent + types moved to lib/agent/agentClient.ts so DesignerChat
// uses the same code path (graph-powered everywhere — no keyword stubs).

interface AgentsChatProps {
  readonly compact?: boolean;
  /** When set, biases the first auto-routing toward this agent — used
   *  by ContextualChat (Gantt → schedule-synth, BOM → budget-pull,
   *  Approvals → approval-age, Design → spatial-fit-check). The
   *  full keyword router still wins if the user's question clearly
   *  targets a different specialist. */
  readonly defaultAgentId?: string;
  /** Override the default suggested prompts so they match the
   *  surface's context. */
  readonly suggestedPrompts?: ReadonlyArray<string>;
}

export function AgentsChat({ compact = false, defaultAgentId, suggestedPrompts: suggestedOverride }: AgentsChatProps): JSX.Element {
  const { graph: baseGraph, activeId } = useActiveProjectGraph();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = (): void => setTick((t) => t + 1);
    window.addEventListener("foyrmind:timeline", bump);
    window.addEventListener("foyrmind:projectChanged", bump);
    return () => {
      window.removeEventListener("foyrmind:timeline", bump);
      window.removeEventListener("foyrmind:projectChanged", bump);
    };
  }, []);
  const graph = useMemo(() => { void tick; return mergeProjectLayers(baseGraph, activeId); }, [baseGraph, activeId, tick]);

  // Conversational history per project — persisted so context survives
  // tab switches and refreshes. Memory is the user's stated requirement:
  // every turn sends the recent history to the agent. The cluster brain
  // also stores its own typed :MemoryCard nodes; this is the client side.
  const historyKey = `foyrmind.chatHistory.${activeId}`;
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(historyKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as ChatMessage[];
      return Array.isArray(parsed) ? parsed.slice(-40) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(historyKey, JSON.stringify(messages.slice(-40))); } catch { /* ignore quota */ }
  }, [historyKey, messages]);
  // Re-read history when the active project changes.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(historyKey);
      setMessages(raw ? (JSON.parse(raw) as ChatMessage[]).slice(-40) : []);
    } catch { setMessages([]); }
  }, [historyKey]);

  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll the message list when new ones arrive.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  /** Send a turn to the real agent endpoint. Imperative write actions
   *  (e.g. "add task X") are detected client-side and persisted via
   *  addUserTask; everything else hits the LightRAG-backed brain.
   *  When the brain is offline we surface that HONESTLY — never a
   *  pattern-matched pretend answer. CLAUDE.md no-stubs. */
  async function send(text: string): Promise<void> {
    const trimmed = text.trim();
    if (trimmed.length === 0 || busy) return;
    const userId = (typeof window !== "undefined" && window.localStorage.getItem("foyrmind.userEmail")) || "designer";
    const userPersona = resolveActor("human", userId);
    const ts = new Date().toISOString();
    const userMsg: ChatMessage = {
      id: `cm-${Date.now()}-u`,
      role: "user",
      text: trimmed,
      actorId: userId,
      persona: userPersona,
      ts,
    };
    setMessages((prev) => [...prev, userMsg]);
    setDraft("");

    recordEvent(activeId, {
      actorKind: "human", actorId: userId, verb: "viewed",
      subjectKind: "Project", subjectId: graph.projectId, subjectLabel: graph.projectName,
      verdict: "info",
      summary: `asked: ${trimmed.slice(0, 80)}${trimmed.length > 80 ? "..." : ""}`,
    });

    // Client-side imperative — typed write actions land instantly,
    // even if the agent brain is offline (they're project-graph
    // mutations, not LLM-derived).
    const write = detectWriteAction(trimmed);
    if (write && write.kind === "addTask") {
      const task = addUserTask(activeId, {
        title: write.title,
        source: defaultAgentId ? `chat:${defaultAgentId}` : "chat",
        ...(write.dueHint ? { dueOn: write.dueHint } : {}),
      });
      const persona = resolveActor("agent", "approval-age");
      const reply: ChatMessage = {
        id: `cm-${Date.now()}-a`,
        role: "agent",
        text: `Added typed Task "${task.title}" to ${graph.projectName}. ${write.dueHint ? `Due hint: "${write.dueHint}".` : ""} It surfaces immediately on Tracker → Tasks and Alerts.`,
        actorId: "approval-age",
        persona,
        ts: new Date().toISOString(),
        cited: ["user/chat-write-action"],
      };
      setMessages((prev) => [...prev, reply]);
      recordEvent(activeId, {
        actorKind: "agent", actorId: "approval-age", verb: "added",
        subjectKind: "Alert", subjectId: task.id, subjectLabel: task.title,
        verdict: "info",
        summary: `Task created via chat: "${task.title}".`,
        citation: "user/chat-write-action",
      });
      return;
    }

    // Real agent path — LightRAG + nemotron-nvfp4 via APISIX. History
    // is sent so the cluster brain has conversational context. We ALSO
    // send a compact slice of the project's typed TimelineEvent nodes
    // so LightRAG (Tantivy sparse + bge-m3 dense) can index every prior
    // human edit AND agent fire — chat history flows back into the graph.
    setBusy(true);
    const history = messages.slice(-12).map((m) => ({ role: m.role, text: m.text, actorId: m.actorId, ts: m.ts }));
    // Compact representation of typed TimelineEvents on this project —
    // the brain reads these as graph context (Tantivy keyword recall +
    // bge-m3 dense retrieval over the typed hypergraph).
    const timelineSnapshot = graph.nodes
      .filter((n) => n.kind === "TimelineEvent")
      .slice(-20)
      .map((n) => ({
        id: n.id,
        verb: typeof n.attrs["verb"] === "string" ? n.attrs["verb"] as string : "",
        actorKind: typeof n.attrs["actorKind"] === "string" ? n.attrs["actorKind"] as string : "",
        actorId: typeof n.attrs["actorId"] === "string" ? n.attrs["actorId"] as string : "",
        summary: typeof n.attrs["summary"] === "string" ? n.attrs["summary"] as string : "",
        atTime: typeof n.attrs["atTime"] === "string" ? n.attrs["atTime"] as string : "",
        ...(typeof n.attrs["citation"] === "string" ? { citation: n.attrs["citation"] as string } : {}),
      }));
    const reply = await askAgent({
      projectId: activeId,
      question: trimmed,
      history,
      defaultAgentId: defaultAgentId,
      timelineSnapshot,
    });
    setBusy(false);

    if (reply.ok) {
      const persona = resolveActor("agent", reply.actorId);
      const m: ChatMessage = {
        id: `cm-${Date.now()}-a`,
        role: "agent",
        text: reply.text,
        actorId: reply.actorId,
        persona,
        ts: new Date().toISOString(),
        cited: reply.cited,
      };
      setMessages((prev) => [...prev, m]);
      recordEvent(activeId, {
        actorKind: "agent", actorId: reply.actorId, verb: "drafted",
        subjectKind: "Project", subjectId: graph.projectId, subjectLabel: graph.projectName,
        verdict: "info",
        summary: reply.text.slice(0, 140),
        ...(reply.cited[0] !== undefined ? { citation: reply.cited[0] } : {}),
      });
      return;
    }

    // Agent brain offline — honest message, no pattern-matched answer.
    const persona = resolveActor("agent", defaultAgentId ?? "supervisor");
    const offline: ChatMessage = {
      id: `cm-${Date.now()}-a`,
      role: "agent",
      text: `Agent brain not bound yet (HTTP ${reply.status}). ${reply.detail}`,
      actorId: "supervisor",
      persona: { ...persona, displayName: "Supervisor", title: "LightRAG + nemotron-nvfp4 via APISIX (offline)" },
      ts: new Date().toISOString(),
      cited: ["agent/not-bound"],
    };
    setMessages((prev) => [...prev, offline]);
    recordEvent(activeId, {
      actorKind: "agent", actorId: "supervisor", verb: "rejected",
      subjectKind: "Project", subjectId: graph.projectId, subjectLabel: graph.projectName,
      verdict: "warn",
      summary: `Agent brain offline: ${reply.detail.slice(0, 140)}`,
      citation: "agent/not-bound",
    });
  }

  function clearHistory(): void {
    setMessages([]);
    try { window.localStorage.removeItem(historyKey); } catch { /* ignore */ }
  }

  const defaultSuggestions: ReadonlyArray<string> = [
    "How is the budget looking?",
    "Any lead-time risk for the kitchen?",
    "Are there approvals pending too long?",
    "Tell me about the schedule and Handover date.",
    "Does the kitchen work-triangle pass NKBA?",
    "Is the style cohesion holding up?",
  ];
  const suggestedPrompts = suggestedOverride ?? defaultSuggestions;

  return (
    <section className={"panel p-3 space-y-2.5 " + (compact ? "" : "min-h-[460px]")}>
      <header className="flex items-start justify-between gap-3 px-1">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5">
            <MessageSquare className="w-3 h-3" /> Chat with the team
          </div>
          <h3 className="font-newyork text-[22px] text-ink-950 leading-tight mt-0.5">
            Ask any specialist on {graph.projectName}
          </h3>
          <div className="text-[11px] text-ink-500 mt-1">
            Every answer comes from a graph-powered specialist (supervisor + 10 skills, LightRAG over this project's typed hypergraph). Conversation history is persisted per project.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10.5px] tabular text-ink-500">{agentRegistry().length} experts</span>
          {messages.length > 0 ? (
            <button
              type="button"
              onClick={clearHistory}
              title="Clear conversation history (this project only)"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-border bg-cream text-ink-500 hover:text-foyr-deep hover:border-foyr-deep/40 text-[10.5px] transition"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          ) : null}
        </div>
      </header>

      <div ref={scrollRef} className="rounded-xl border border-border bg-cream-dark/30 p-2 space-y-2 max-h-[420px] overflow-y-auto">
        {messages.length === 0 ? (
          <div className="px-2 py-1">
            <div className="text-[11px] text-ink-500 mb-1.5">Try one of these:</div>
            <div className="flex flex-wrap gap-1.5">
              {suggestedPrompts.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => send(p)}
                  className="pill border border-border bg-cream text-ink-700 hover:border-foyr/40 hover:text-ink-950 text-[11px]"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {messages.map((m) => {
          const offline = m.role === "agent" && m.cited?.[0] === "agent/not-bound";
          return (
            <div key={m.id} className="flex items-start gap-2">
              <div
                className={"shrink-0 w-7 h-7 rounded-full grid place-items-center text-[10px] font-semibold text-cream shadow-[0_1px_2px_rgba(0,0,0,0.08)] " + (offline ? "ring-2 ring-foyr-deep/40" : "")}
                style={{ background: m.persona?.color ?? (m.role === "user" ? "#3D6B9D" : "#7A3E5F") }}
                title={`${m.persona?.displayName ?? "—"} · ${m.persona?.title ?? ""}`}
              >
                {offline ? <AlertTriangle className="w-3 h-3" /> : m.persona?.initials ?? (m.role === "user" ? <User className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-ink-500 leading-tight">
                  <span className="font-medium text-ink-950">{m.persona?.displayName ?? (m.role === "user" ? "You" : "Agent")}</span>
                  {m.persona?.title ? <span> · {m.persona.title}</span> : null}
                </div>
                <div className={
                  "text-[12.5px] leading-snug mt-0.5 px-3 py-2 rounded-xl border whitespace-pre-wrap " +
                  (offline
                    ? "bg-foyr-deep/5 border-foyr-deep/30 text-ink-950"
                    : m.role === "user"
                      ? "bg-cream border-border text-ink-950"
                      : "bg-ink-950/[0.025] border-border text-ink-950")
                }>
                  {m.text}
                </div>
                {m.cited && m.cited.length > 0 ? (
                  <div className="text-[9.5px] text-ink-500 tabular mt-1 flex flex-wrap gap-1">
                    {m.cited.map((c) => (
                      <span key={c} className="pill border border-border bg-cream-dark text-ink-500 px-1.5 py-0">{c}</span>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
        {busy ? (
          <div className="flex items-center gap-2 px-2 py-1 text-[11px] text-ink-500 italic">
            <Sparkles className="w-3 h-3 animate-pulse" /> Querying the supervisor + LightRAG over the project graph…
          </div>
        ) : null}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); void send(draft); }}
        className="flex items-center gap-2"
      >
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
          placeholder={busy ? "Supervisor is thinking…" : "Ask any specialist, or type \"add task X before handover\""}
          className="flex-1 px-3 py-2 rounded-xl border border-border bg-cream text-[12.5px] text-ink-950 placeholder:text-ink-500 focus:outline-none focus:border-foyr/40 disabled:bg-cream-dark/40"
        />
        <button
          type="submit"
          disabled={draft.trim().length === 0 || busy}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-ink-950 text-cream text-[12px] hover:bg-foyr-deep transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className={"w-3.5 h-3.5 " + (busy ? "animate-pulse" : "")} /> Ask
        </button>
      </form>
    </section>
  );
}
